/**
 * Reports Data Service
 * Handles all report data fetching, processing, and aggregation
 */

import axios from 'axios';

class ReportsDataService {
  constructor() {
    this.cache = new Map();
    this.cacheTimeout = 1 * 60 * 1000; // 1 minute for reports (reduced for development)
    this.apiEndpoints = {
      'daily-log': '/api/reports/daily-log',
      'revenue': '/api/reports/revenue', 
      'employee-performance': '/api/reports/employee-performance',
      'cart-utilization': '/api/reports/cart-utilization',
      'customer-activity': '/api/reports/customer-activity',
      'service-attendees': '/api/reports/service-attendees',
      'inventory-report': '/api/inventory/items/stats/overview',
      'reservation-stats': '/api/reservations/integration/stats/overview'
    };
  }

  /**
   * Generate a report with caching (DISABLED - always fetch fresh data)
   */
  async generateReport(reportType, parameters = {}, token = null, forceRefresh = false) {
    // Caching disabled - always fetch fresh data
    // const cacheKey = this.generateCacheKey(reportType, parameters);
    // const cached = this.cache.get(cacheKey);
    // if (!forceRefresh && cached && Date.now() - cached.timestamp < this.cacheTimeout) {
    //   return { ...cached.data, fromCache: true };
    // }

    try {
      const endpoint = this.apiEndpoints[reportType];
      if (!endpoint) {
        throw new Error(`Unknown report type: ${reportType}`);
      }

      // Get token from localStorage if not provided
      const authToken = token || localStorage.getItem('token');
      
      const config = {
        params: parameters
      };
      
      // If explicit token is provided, use it; otherwise rely on axios defaults
      if (token) {
        config.headers = {
          Authorization: `Bearer ${token}`
        };
      }
      // If no explicit token but we have a stored token, ensure axios defaults are set
      else if (authToken && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      }

      console.log('🔍 ReportsDataService making request:', {
        endpoint,
        hasToken: !!authToken,
        tokenLength: authToken ? authToken.length : 0,
        hasAxiosDefaults: !!axios.defaults.headers.common['Authorization'],
        config,
        reportType
      });

      const response = await axios.get(endpoint, config);
      
      const reportData = {
        type: reportType,
        parameters,
        data: response.data,
        generatedAt: new Date(),
        fromCache: false
      };

      // Caching disabled - skip cache storage
      // this.cache.set(cacheKey, {
      //   data: reportData,
      //   timestamp: Date.now()
      // });

      return reportData;
    } catch (error) {
      console.error(`🚨 Error generating ${reportType} report:`, {
        message: error.message,
        status: error.response?.status,
        statusText: error.response?.statusText,
        responseData: error.response?.data,
        responseHeaders: error.response?.headers,
        requestConfig: error.config,
        hasToken: !!(token || localStorage.getItem('token'))
      });
      throw new Error(`Failed to generate ${reportType} report: ${error.response?.data?.message || error.message}`);
    }
  }

  /**
   * Fetch daily log report
   */
  async getDailyLog(startDate, endDate = null) {
    const params = { startDate };
    if (endDate) {
      params.endDate = endDate;
    }
    return await this.generateReport('daily-log', params);
  }

  /**
   * Fetch revenue report
   */
  async getRevenueReport(startDate = null, endDate = null, serviceId = null) {
    const params = {};
    if (startDate) params.startDate = startDate;
    if (endDate) params.endDate = endDate;
    if (serviceId) params.serviceId = serviceId;

    return await this.generateReport('revenue', params);
  }

  /**
   * Fetch employee performance report
   */
  async getEmployeePerformance(startDate, endDate) {
    return await this.generateReport('employee-performance', { startDate, endDate });
  }

  /**
   * Fetch cart utilization report
   */
  async getCartUtilization(startDate, endDate) {
    return await this.generateReport('cart-utilization', { startDate, endDate });
  }

  /**
   * Fetch customer activity report
   */
  async getCustomerActivity(startDate, endDate) {
    return await this.generateReport('customer-activity', { startDate, endDate });
  }

  /**
   * Fetch aggregated dashboard data for reports
   */
  async getReportsDashboardData(token = null) {
    try {
      // Get token from localStorage if not provided
      const authToken = token || localStorage.getItem('token');
      
      const config = {};
      
      // If explicit token is provided, use it; otherwise rely on axios defaults
      if (token) {
        config.headers = {
          Authorization: `Bearer ${token}`
        };
      }
      // If no explicit token but we have a stored token, ensure axios defaults are set
      else if (authToken && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${authToken}`;
      }
      
      const [inventoryRes, reservationRes] = await Promise.allSettled([
        axios.get(this.apiEndpoints['inventory-report'], config),
        axios.get(this.apiEndpoints['reservation-stats'], config)
      ]);

      return {
        inventory: inventoryRes.status === 'fulfilled' ? inventoryRes.value.data : null,
        reservations: reservationRes.status === 'fulfilled' ? reservationRes.value.data : null,
        generatedAt: new Date()
      };
    } catch (error) {
      console.error('Error fetching reports dashboard data:', error);
      throw error;
    }
  }

  /**
   * Batch generate multiple reports
   */
  async batchGenerate(reportRequests, token = null) {
    const results = await Promise.allSettled(
      reportRequests.map(request => 
        this.generateReport(request.type, request.parameters, token)
      )
    );

    return results.map((result, index) => ({
      request: reportRequests[index],
      success: result.status === 'fulfilled',
      data: result.status === 'fulfilled' ? result.value : null,
      error: result.status === 'rejected' ? result.reason.message : null
    }));
  }

  /**
   * Validate report parameters
   */
  validateParameters(reportType, parameters) {
    const validations = {
      'daily-log': this.validateDailyLogParams,
      'revenue': this.validateRevenueParams,
      'employee-performance': this.validateDateRangeParams,
      'cart-utilization': this.validateDateRangeParams,
      'customer-activity': this.validateDateRangeParams,
      'service-attendees': this.validateServiceAttendeesParams
    };

    const validator = validations[reportType];
    if (!validator) {
      throw new Error(`No validator found for report type: ${reportType}`);
    }

    return validator(parameters);
  }

  validateDailyLogParams(params) {
    const { startDate, endDate } = params;
    if (!startDate) {
      throw new Error('Start date is required for daily log report');
    }

    const parsedStartDate = new Date(startDate);
    if (isNaN(parsedStartDate.getTime())) {
      throw new Error('Invalid start date format');
    }

    // Validate end date if provided
    if (endDate) {
      const parsedEndDate = new Date(endDate);
      if (isNaN(parsedEndDate.getTime())) {
        throw new Error('Invalid end date format');
      }

      if (parsedStartDate > parsedEndDate) {
        throw new Error('Start date must be before or equal to end date');
      }

      // Check if date range is reasonable (not more than 1 year)
      const oneYear = 365 * 24 * 60 * 60 * 1000;
      if (parsedEndDate - parsedStartDate > oneYear) {
        throw new Error('Date range cannot exceed 1 year');
      }
    }

    return true;
  }

  validateDateRangeParams(params) {
    const { startDate, endDate } = params;
    
    if (!startDate || !endDate) {
      throw new Error('Both startDate and endDate are required');
    }

    const start = new Date(startDate);
    const end = new Date(endDate);

    if (isNaN(start.getTime()) || isNaN(end.getTime())) {
      throw new Error('Invalid date format');
    }

    if (start > end) {
      throw new Error('Start date must be before end date');
    }

    // Check if date range is reasonable (not more than 1 year)
    const oneYear = 365 * 24 * 60 * 60 * 1000;
    if (end - start > oneYear) {
      throw new Error('Date range cannot exceed 1 year');
    }

    return true;
  }

  validateRevenueParams(params) {
    const { startDate, endDate, serviceId } = params;

    // Revenue report now allows optional dates
    if (startDate || endDate) {
      if (startDate && endDate) {
        const start = new Date(startDate);
        const end = new Date(endDate);

        if (isNaN(start.getTime()) || isNaN(end.getTime())) {
          throw new Error('Invalid date format');
        }

        if (start > end) {
          throw new Error('Start date must be before end date');
        }

        // Check if date range is reasonable (not more than 1 year)
        const oneYear = 365 * 24 * 60 * 60 * 1000;
        if (end - start > oneYear) {
          throw new Error('Date range cannot exceed 1 year');
        }
      } else if (startDate && !endDate) {
        const start = new Date(startDate);
        if (isNaN(start.getTime())) {
          throw new Error('Invalid start date format');
        }
      } else if (!startDate && endDate) {
        const end = new Date(endDate);
        if (isNaN(end.getTime())) {
          throw new Error('Invalid end date format');
        }
      }
    }

    return true;
  }

  validateServiceAttendeesParams(params) {
    // Service attendees report doesn't require any parameters
    // It fetches all service reservations for the organization
    return true;
  }

  /**
   * Generate cache key for report
   */
  generateCacheKey(reportType, parameters) {
    const sortedParams = Object.keys(parameters)
      .sort()
      .map(key => `${key}=${parameters[key]}`)
      .join('&');
    return `${reportType}:${sortedParams}`;
  }

  /**
   * Clear cache for specific report type or all
   */
  clearCache(reportType = null) {
    if (reportType) {
      const keysToDelete = Array.from(this.cache.keys())
        .filter(key => key.startsWith(`${reportType}:`));
      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get cache statistics
   */
  getCacheStats() {
    const entries = Array.from(this.cache.entries());
    const now = Date.now();
    
    return {
      totalEntries: entries.length,
      validEntries: entries.filter(([_, value]) => 
        now - value.timestamp < this.cacheTimeout
      ).length,
      expiredEntries: entries.filter(([_, value]) => 
        now - value.timestamp >= this.cacheTimeout
      ).length,
      cacheSize: entries.reduce((size, [key, value]) => 
        size + JSON.stringify(value).length, 0
      )
    };
  }
}

export default new ReportsDataService();